<svelte:options
  customElement={{
    shadow: 'open',
    props: {
      status: { type: 'String' },
      lang: { type: 'String' },
      gameName: { type: 'String' },
      tryAgainHref: { type: 'String' },
      ctaText: { type: 'String' },
      ctaHref: { type: 'String' },
      theme: { type: 'String' }
    }
  }}
/>

<script lang="ts">
  import { onMount } from 'svelte';
  import { getPaymentReturnTranslations, type PaymentReturnLanguage } from './translations';
  import { parseTemplate } from './template';

  type PaymentReturnStatus =
    'cancelled' | 'await_email' | 'success' | 'error' | 'active_subscription';

  const ACTIVE_SUBSCRIPTION_HREF = 'https://account.teuteuf.fr/#subscription';
  const MEMBERS_EMAIL = 'members@teuteuf.fr';

  const INRIA_SANS_HREF =
    'https://fonts.googleapis.com/css2?family=Inria+Sans:wght@300;400;700&display=swap';
  const STYLESHEET_ID = 'teuteuf-payment-return-inria-sans';
  const PRECONNECT_ID = 'teuteuf-payment-return-gstatic-preconnect';

  function loadInriaSans() {
    if (typeof document === 'undefined') return;

    if (!document.getElementById(PRECONNECT_ID)) {
      const preconnect = document.createElement('link');
      preconnect.id = PRECONNECT_ID;
      preconnect.rel = 'preconnect';
      preconnect.href = 'https://fonts.gstatic.com';
      preconnect.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect);
    }

    if (!document.getElementById(STYLESHEET_ID)) {
      const link = document.createElement('link');
      link.id = STYLESHEET_ID;
      link.rel = 'stylesheet';
      link.href = INRIA_SANS_HREF;
      document.head.appendChild(link);
    }
  }

  onMount(loadInriaSans);

  let {
    status = 'success',
    lang = 'en',
    gameName = 'your game',
    tryAgainHref,
    ctaText,
    ctaHref,
    theme = 'auto'
  }: {
    /** Payment state shown by the component */
    status?: PaymentReturnStatus | string;
    /** Translation language. Unsupported values fall back to 'en'. */
    lang?: PaymentReturnLanguage | string;
    /** Game name included in the successful-payment message. */
    gameName?: string;
    /** Destination for the retry link shown after a cancelled payment. usually passport offer page */
    tryAgainHref?: string;
    /** Label for the CTA shown after a successful payment. */
    ctaText?: string;
    /** Destination for the CTA shown after a successful payment. */
    ctaHref?: string;
    /** Visual theme. `auto` follows the user's system color scheme. */
    theme?: 'light' | 'dark' | 'auto';
  } = $props();

  let systemPrefersDark = $state(false);
  $effect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => (systemPrefersDark = mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  });

  const isDark = $derived(theme === 'dark' || (theme === 'auto' && systemPrefersDark));
  const t = $derived(getPaymentReturnTranslations(lang));
  const statusKind = $derived<PaymentReturnStatus>(
    status === 'cancelled'
      ? 'cancelled'
      : status === 'await_email'
        ? 'await_email'
        : status === 'error'
          ? 'error'
          : status === 'active_subscription'
            ? 'active_subscription'
            : 'success'
  );
  const hasCta = $derived(
    statusKind === 'cancelled'
      ? Boolean(tryAgainHref?.trim())
      : statusKind === 'success'
        ? Boolean(ctaText?.trim() && ctaHref?.trim())
        : statusKind === 'active_subscription'
  );
  const successEnjoyParts = $derived(parseTemplate(t.successEnjoy));
  const activeSubscriptionAdsNoticeParts = $derived(parseTemplate(t.activeSubscriptionAdsNotice));
</script>

{#snippet renderTemplate(parts: ReturnType<typeof parseTemplate>)}
  {#each parts as part, i (i)}
    {#if part.type === 'param' && part.key === 'gamename'}
      {gameName}
    {:else if part.type === 'param' && part.key === 'email'}
      <a href="mailto:{MEMBERS_EMAIL}">{MEMBERS_EMAIL}</a>
    {:else if part.type === 'text'}
      {part.value}
    {/if}
  {/each}
{/snippet}

<main
  class="payment-return"
  class:dark={isDark}
  class:cancelled={statusKind === 'cancelled'}
  class:error={statusKind === 'error'}
>
  <div class="payment-return__rail" aria-hidden="true">
    <span class="payment-return__rail-mark">
      {#if statusKind === 'await_email'}
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2"></rect>
          <path d="m4 7 8 6 8-6"></path>
        </svg>
      {:else if statusKind === 'cancelled' || statusKind === 'error'}
        !
      {:else}
        ✓
      {/if}
    </span>
    <span class="payment-return__rail-line"></span>
  </div>

  <div class="payment-return__content">
    <header class="payment-return__header" aria-labelledby="payment-return-title">
      <h1 id="payment-return-title">
        {#if statusKind === 'cancelled'}
          {t.cancelledTitle}
        {:else if statusKind === 'await_email'}
          {t.awaitEmailTitle}
        {:else if statusKind === 'error'}
          {t.errorTitle}
        {:else if statusKind === 'active_subscription'}
          {t.activeSubscriptionTitle}
        {:else}
          {t.successTitle}
        {/if}
      </h1>
    </header>

    <section class="payment-return__message" aria-live="polite">
      {#if statusKind === 'cancelled'}
        <p>
          {t.cancelledMessage}
        </p>
      {:else if statusKind === 'await_email'}
        <p>
          {t.awaitEmailMessage}
        </p>
        <p>
          {t.awaitEmailSupport} <a href="mailto:premium@teuteuf.fr">members@teuteuf.fr</a>
        </p>
      {:else if statusKind === 'error'}
        <p>
          {t.errorMessage} <a href="mailto:members@teuteuf.fr">members@teuteuf.fr</a>.
        </p>
      {:else if statusKind === 'active_subscription'}
        <p>
          {t.activeSubscriptionMessage}
        </p>
        <p>
          {@render renderTemplate(activeSubscriptionAdsNoticeParts)}
        </p>
      {:else}
        <p>
          {t.successMessage}
        </p>
        <p class="payment-return__no-ads">
          {@render renderTemplate(successEnjoyParts)} <strong>{t.adFree}!</strong>
        </p>
      {/if}
    </section>

    {#if hasCta}
      <a
        class="payment-return__cta"
        href={statusKind === 'cancelled'
          ? tryAgainHref
          : statusKind === 'active_subscription'
            ? ACTIVE_SUBSCRIPTION_HREF
            : ctaHref}
      >
        <span>
          {statusKind === 'cancelled'
            ? t.cancelledCta
            : statusKind === 'active_subscription'
              ? t.activeSubscriptionCta
              : ctaText}
        </span>
        <span class="payment-return__cta-arrow" aria-hidden="true">&#8594;</span>
      </a>
    {/if}
  </div>
</main>

<style>
  :host {
    display: block;
    width: 100%;
  }

  .payment-return {
    --payment-ink: #17233d;
    --payment-muted: #444d5c;
    --payment-accent: #2563eb;
    --payment-accent-hover: #1d4ed8;
    --payment-paper: #fbfcff;
    --payment-border: rgba(37, 99, 235, 0.16);
    --payment-section-border: rgba(37, 99, 235, 0.2);
    --payment-focus-ring: rgba(37, 99, 235, 0.45);
    --payment-radius: 0.45rem;
    display: grid;
    grid-template-columns: 2.75rem minmax(0, 1fr);
    gap: 1rem;
    container-type: inline-size;
    width: 100%;
    box-sizing: border-box;
    margin-top: 0.5rem;
    padding: 1.25rem 1.25rem 1.35rem;
    color: var(--payment-ink);
    background:
      linear-gradient(110deg, rgba(219, 234, 254, 0.36), transparent 42%), var(--payment-paper);
    border: 1px solid var(--payment-border);
    border-radius: var(--payment-radius);
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
      'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .payment-return *,
  .payment-return *::before,
  .payment-return *::after {
    box-sizing: border-box;
  }

  .payment-return.dark {
    --payment-ink: #f1f5ff;
    --payment-muted: #c7d1e3;
    --payment-paper: #151e30;
    --payment-border: rgba(147, 197, 253, 0.2);
    --payment-section-border: rgba(147, 197, 253, 0.3);
    background:
      linear-gradient(110deg, rgba(37, 99, 235, 0.14), transparent 42%), var(--payment-paper);
  }

  .payment-return.cancelled {
    --payment-accent: #b45309;
    --payment-accent-hover: #92400e;
    --payment-focus-ring: rgba(180, 83, 9, 0.42);
  }

  .payment-return.error {
    --payment-accent: #dc2626;
    --payment-accent-hover: #b91c1c;
    --payment-focus-ring: rgba(220, 38, 38, 0.42);
  }

  .payment-return__rail {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 0.15rem;
  }

  .payment-return__rail-mark {
    display: grid;
    width: 2.25rem;
    height: 2.25rem;
    place-items: center;
    border: 1px solid var(--payment-accent);
    border-radius: 50%;
    color: var(--payment-accent);
    font-family: 'Inria Sans', sans-serif;
    font-size: 1rem;
    font-weight: 700;
  }

  .payment-return__rail-mark svg {
    width: 1.15rem;
    height: 1.15rem;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.6;
  }

  .payment-return__rail-line {
    width: 1px;
    flex: 1;
    margin-top: 0.65rem;
    background: var(--payment-section-border);
  }

  .payment-return__header {
    padding: 0.05rem 0 1rem;
  }

  .payment-return h1 {
    max-width: 28rem;
    margin: 0;
    font-family: 'Inria Sans', sans-serif;
    font-size: clamp(1.8rem, 5vw, 2.6rem);
    font-weight: 800;
    letter-spacing: -0.05em;
    line-height: 0.98;
  }

  .payment-return__message {
    max-width: 34rem;
    border-top: 1px solid var(--payment-section-border);
    padding: 0.95rem 0 0.2rem;
    color: var(--payment-muted);
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .payment-return__message p {
    margin: 0;
  }

  .payment-return__message p + p {
    margin-top: 0.7rem;
  }

  .payment-return__message a {
    color: var(--payment-ink);
    font-weight: 800;
    text-decoration: underline;
    text-decoration-thickness: 0.1em;
    text-underline-offset: 0.15em;
  }

  .payment-return__message a:hover,
  .payment-return__message a:focus-visible {
    color: var(--payment-accent);
  }

  .payment-return__no-ads {
    color: var(--payment-ink);
  }

  .payment-return__no-ads strong {
    color: var(--payment-accent);
    font-weight: 800;
  }

  .payment-return__cta {
    display: inline-flex;
    width: fit-content;
    align-items: center;
    gap: 0.45rem;
    margin-top: 0.85rem;
    border-bottom: 2px solid currentColor;
    padding: 0.25rem 0 0.2rem;
    color: var(--payment-ink);
    font-size: 0.92rem;
    font-weight: 800;
    text-decoration: none;
    transition:
      color 160ms ease,
      border-color 160ms ease;
  }

  .payment-return__cta:hover {
    border-color: var(--payment-accent);
    color: var(--payment-accent);
  }

  .payment-return__cta:focus-visible {
    border-radius: 0.2rem;
    outline: 3px solid var(--payment-focus-ring);
    outline-offset: 4px;
  }

  .payment-return__cta-arrow {
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Segoe UI Symbol', 'Apple Symbols',
      'Noto Sans Symbols', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 1rem;
    transition: transform 160ms ease;
  }

  .payment-return__cta:hover .payment-return__cta-arrow {
    transform: translateX(0.15rem);
  }

  @container (max-width: 440px) {
    .payment-return {
      grid-template-columns: 1.75rem minmax(0, 1fr);
      gap: 0.7rem;
      padding: 1rem;
    }

    .payment-return__rail-mark {
      width: 1.75rem;
      height: 1.75rem;
      font-size: 0.85rem;
    }

    .payment-return__rail-mark svg {
      width: 0.95rem;
      height: 0.95rem;
    }

    .payment-return__header {
      padding-bottom: 0.75rem;
    }

    .payment-return__message {
      font-size: 0.84rem;
      line-height: 1.4;
    }
  }

  @media (max-width: 380px) {
    .payment-return {
      padding-inline: 0.75rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .payment-return__cta,
    .payment-return__cta-arrow {
      transition: none;
    }
  }
</style>
